import { useState, useEffect, useCallback } from "react";
import { Settings as SettingsIcon, MapPin, Plus, Trash2, Pencil, Check, X, DollarSign, Stethoscope, HelpCircle, MessageSquare, Send, ChevronDown, ChevronUp, CreditCard, Crown, Zap, RefreshCw, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { saveTaxRate, saveWrvuRate, sendMessageToAdmin, fetchMyMessages, fetchSubscriptionStatus, createPortalSession, createCheckoutSession } from "@/lib/api";
import { useTheme } from "@/contexts/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";
import { CPT_LOOKUP } from "@/lib/cptLookup";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Helper function to get auth token
const getAuthToken = () => {
  const authData = localStorage.getItem("wrvu_auth") || sessionStorage.getItem("wrvu_auth");
  return authData ? JSON.parse(authData).token : null;
};

// Color options for Quick-Add Visit Types
const COLOR_OPTIONS = [
  { value: "blue", label: "Blue", bgClass: "bg-blue-500", borderClass: "border-blue-500" },
  { value: "emerald", label: "Green", bgClass: "bg-emerald-500", borderClass: "border-emerald-500" },
  { value: "orange", label: "Orange", bgClass: "bg-orange-500", borderClass: "border-orange-500" },
  { value: "red", label: "Red", bgClass: "bg-red-500", borderClass: "border-red-500" },
  { value: "yellow", label: "Yellow", bgClass: "bg-yellow-400", borderClass: "border-yellow-400" },
  { value: "pink", label: "Pink", bgClass: "bg-pink-400", borderClass: "border-pink-400" },
];

// Get color styling for display
const getColorStyles = (color) => {
  const colorMap = {
    blue: { text: "text-blue-400", border: "border-blue-500/30", bg: "bg-blue-500/10" },
    emerald: { text: "text-emerald-400", border: "border-emerald-500/30", bg: "bg-emerald-500/10" },
    orange: { text: "text-orange-400", border: "border-orange-500/30", bg: "bg-orange-500/10" },
    red: { text: "text-red-400", border: "border-red-500/30", bg: "bg-red-500/10" },
    yellow: { text: "text-yellow-400", border: "border-yellow-400/30", bg: "bg-yellow-400/10" },
    pink: { text: "text-pink-400", border: "border-pink-400/30", bg: "bg-pink-400/10" },
    rose: { text: "text-pink-400", border: "border-pink-400/30", bg: "bg-pink-400/10" }, // legacy support
  };
  return colorMap[color] || colorMap.blue;
};

export default function Settings({ taxRate, wrvuRate, onTaxRateChanged, onWrvuRateChanged, onQuickAddVisitsChanged, onOpenTutorial }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const currentYear = new Date().getFullYear();
  
  // Clinic locations
  const [clinicLocations, setClinicLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [newLocation, setNewLocation] = useState("");
  const [addingLocation, setAddingLocation] = useState(false);

  // Tax rate editing
  const [editingTax, setEditingTax] = useState(false);
  const [tempTaxRate, setTempTaxRate] = useState(String(taxRate));
  const [savingTax, setSavingTax] = useState(false);

  // wRVU rate editing
  const [editingWrvu, setEditingWrvu] = useState(false);
  const [tempWrvuRate, setTempWrvuRate] = useState(String(wrvuRate));
  const [savingWrvu, setSavingWrvu] = useState(false);
  const [showWrvuConfirm, setShowWrvuConfirm] = useState(false);
  const [pendingWrvuRate, setPendingWrvuRate] = useState(null);

  // Quick-Add Visit Types
  const [quickAddVisits, setQuickAddVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(true);
  const [editingVisitIndex, setEditingVisitIndex] = useState(null);
  const [tempVisit, setTempVisit] = useState(null);
  const [savingVisit, setSavingVisit] = useState(false);

  // Messaging Admin
  const [messageContent, setMessageContent] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [myMessages, setMyMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showMessages, setShowMessages] = useState(false);

  // Subscription management
  const [subStatus, setSubStatus] = useState(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [loadingUpgrade, setLoadingUpgrade] = useState(false);

  // Load clinic locations
  const loadClinicLocations = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setLoadingLocations(false);
      return;
    }
    setLoadingLocations(true);
    try {
      const res = await fetch(`${API_URL}/api/clinic-locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClinicLocations(data);
      }
    } catch {
      setClinicLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  // Load Quick-Add Visit Types
  const loadQuickAddVisits = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setLoadingVisits(false);
      return;
    }
    setLoadingVisits(true);
    try {
      const res = await fetch(`${API_URL}/api/quick-add-visits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setQuickAddVisits(data);
      }
    } catch {
      setQuickAddVisits([]);
    } finally {
      setLoadingVisits(false);
    }
  }, []);

  useEffect(() => {
    // Small delay to ensure token is available after login
    const timer = setTimeout(() => {
      loadClinicLocations();
      loadQuickAddVisits();
      fetchSubscriptionStatus().then(setSubStatus).catch(() => {});
    }, 100);
    return () => clearTimeout(timer);
  }, [loadClinicLocations, loadQuickAddVisits]);

  useEffect(() => {
    setTempTaxRate(String(taxRate));
  }, [taxRate]);

  useEffect(() => {
    setTempWrvuRate(String(wrvuRate));
  }, [wrvuRate]);

  // Clinic location handlers
  const handleAddLocation = async () => {
    if (!newLocation.trim()) {
      toast.error("Please enter a location name");
      return;
    }
    setAddingLocation(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/api/clinic-locations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: newLocation.trim() })
      });
      if (res.ok) {
        const data = await res.json();
        setClinicLocations(prev => [...prev, data]);
        setNewLocation("");
        toast.success("Clinic location added");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add location");
      }
    } catch {
      toast.error("Failed to add location");
    } finally {
      setAddingLocation(false);
    }
  };

  const handleDeleteLocation = async (id) => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/api/clinic-locations/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setClinicLocations(prev => prev.filter(loc => loc.id !== id));
        toast.success("Clinic location deleted");
      }
    } catch {
      toast.error("Failed to delete location");
    }
  };

  // Tax rate handlers
  const handleSaveTaxRate = async () => {
    const parsed = parseFloat(tempTaxRate);
    if (isNaN(parsed) || parsed < 0 || parsed > 100) {
      toast.error("Tax rate must be between 0 and 100");
      return;
    }
    setSavingTax(true);
    try {
      await saveTaxRate(currentYear, parsed);
      if (onTaxRateChanged) onTaxRateChanged(parsed);
      setEditingTax(false);
      toast.success(`Tax rate saved: ${parsed}% for ${currentYear}`);
    } catch {
      toast.error("Failed to save tax rate");
    } finally {
      setSavingTax(false);
    }
  };

  // wRVU rate handlers
  const handleWrvuRateSubmit = () => {
    const parsed = parseFloat(tempWrvuRate);
    if (isNaN(parsed) || parsed < 0) {
      toast.error("wRVU rate must be a positive number");
      return;
    }
    setPendingWrvuRate(parsed);
    setShowWrvuConfirm(true);
  };

  const handleSaveWrvuRate = async (applyToPrevious) => {
    setSavingWrvu(true);
    setShowWrvuConfirm(false);
    try {
      await saveWrvuRate(currentYear, pendingWrvuRate, applyToPrevious);
      if (onWrvuRateChanged) onWrvuRateChanged(pendingWrvuRate);
      setEditingWrvu(false);
      if (applyToPrevious) {
        toast.success(`wRVU rate updated to $${pendingWrvuRate}/wRVU for all shifts (past & future)`);
      } else {
        toast.success(`wRVU rate updated to $${pendingWrvuRate}/wRVU from today onwards`);
      }
    } catch {
      toast.error("Failed to save wRVU rate");
    } finally {
      setSavingWrvu(false);
      setPendingWrvuRate(null);
    }
  };

  // Quick-Add Visit Type handlers
  const handleEditVisit = (index) => {
    setEditingVisitIndex(index);
    setTempVisit({ ...quickAddVisits[index] });
  };

  const handleCancelEditVisit = () => {
    setEditingVisitIndex(null);
    setTempVisit(null);
  };

  const handleSaveVisit = async () => {
    if (!tempVisit.code.trim()) {
      toast.error("CPT code is required");
      return;
    }
    if (tempVisit.wrvu < 0) {
      toast.error("wRVU must be 0 or greater");
      return;
    }

    setSavingVisit(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/api/quick-add-visits/${editingVisitIndex}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(tempVisit)
      });

      if (res.ok) {
        const updatedVisits = await res.json();
        setQuickAddVisits(updatedVisits);
        if (onQuickAddVisitsChanged) onQuickAddVisitsChanged(updatedVisits);
        setEditingVisitIndex(null);
        setTempVisit(null);
        toast.success("Visit type updated");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update visit type");
      }
    } catch {
      toast.error("Failed to update visit type");
    } finally {
      setSavingVisit(false);
    }
  };

  const handleTempVisitChange = (field, value) => {
    if (field === "code") {
      // Check if the code exists in CPT_LOOKUP and autofill description and wRVU
      const cptData = CPT_LOOKUP[value];
      if (cptData) {
        setTempVisit(prev => ({
          ...prev,
          code: value,
          description: cptData.name,
          wrvu: cptData.wrvu
        }));
        return;
      }
    }
    setTempVisit(prev => ({ ...prev, [field]: value }));
  };

  // Message Admin handlers
  const loadMyMessages = async () => {
    setLoadingMessages(true);
    try {
      const data = await fetchMyMessages();
      setMyMessages(data);
    } catch {
      setMyMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim()) {
      toast.error("Please enter a message");
      return;
    }
    if (messageContent.length > 1000) {
      toast.error("Message must be 1000 characters or less");
      return;
    }
    setSendingMessage(true);
    try {
      await sendMessageToAdmin(messageContent);
      toast.success("Message sent to admin");
      setMessageContent("");
      loadMyMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const formatMessageDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  // Get color class based on color name
  const getVisitColorClasses = (color) => {
    const styles = getColorStyles(color);
    return `${styles.text} ${styles.border} ${styles.bg}`;
  };

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open billing portal. Please try again.");
    } finally {
      setLoadingPortal(false);
    }
  };

  const handleUpgradeToLifetime = async () => {
    setLoadingUpgrade(true);
    try {
      const { checkout_url } = await createCheckoutSession("lifetime");
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout. Please try again.");
    } finally {
      setLoadingUpgrade(false);
    }
  };

  const getSubBadge = () => {
    if (!subStatus) return null;
    const s = subStatus.status;
    if (s === "admin") return { label: "Admin Access", color: "text-amber-400 bg-amber-500/10 border-amber-500/30" };
    if (s === "lifetime") return { label: "Lifetime Access", color: "text-purple-400 bg-purple-500/10 border-purple-500/30" };
    if (s === "trial") return { label: `Free Trial — ${subStatus.days_remaining ?? 0}d left`, color: "text-blue-400 bg-blue-500/10 border-blue-500/30" };
    if (s === "payment_failed") return { label: "Payment Failed", color: "text-red-400 bg-red-500/10 border-red-500/30" };
    if (s === "subscribed") {
      const type = subStatus.subscription_type;
      return {
        label: type === "yearly" ? "Annual Plan" : "Monthly Plan",
        color: "text-green-400 bg-green-500/10 border-green-500/30"
      };
    }
    return { label: "No Active Plan", color: "text-red-400 bg-red-500/10 border-red-500/30" };
  };

  const subBadge = getSubBadge();
  const canManage = subStatus && !["admin", "lifetime", "no_subscription", "no_user"].includes(subStatus.status);
  const canUpgradeToLifetime = subStatus && ["subscribed", "trial"].includes(subStatus.status);

  return (
    <div className="space-y-8 pb-32">
      <div className="flex items-start justify-between">
        <div>
          <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-slate-100' : 'text-gray-900'}`} style={{ fontFamily: "'Manrope', sans-serif" }}>
            Settings
          </h1>
          <p className={isDark ? 'text-slate-500' : 'text-gray-600'}>Manage your clinic locations and rate configurations</p>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          {onOpenTutorial && (
            <Button
              data-testid="help-tutorial-btn"
              variant="outline"
              onClick={onOpenTutorial}
              className={`flex items-center gap-2 ${
                isDark 
                  ? 'text-slate-400 border-slate-600 hover:bg-slate-700 hover:text-slate-200'
                  : 'text-gray-600 border-gray-300 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <HelpCircle size={18} />
              <span>Help / Tutorial</span>
            </Button>
          )}
        </div>
      </div>

      {/* Rate Configuration Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Tax Rate Config */}
        <div data-testid="tax-rate-config" className={`rounded-xl border p-6 ${
          isDark ? 'border-slate-700/30 bg-slate-800/30' : 'border-gray-200 bg-white'
        }`}>
          <div className="flex items-center gap-2 mb-4">
            <SettingsIcon size={18} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
            <span className={`text-sm uppercase tracking-widest font-semibold ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Tax Rate ({currentYear})</span>
          </div>
          {editingTax ? (
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Input
                  data-testid="tax-rate-input"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={tempTaxRate}
                  onChange={(e) => setTempTaxRate(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSaveTaxRate(); if (e.key === "Escape") setEditingTax(false); }}
                  className={`font-mono text-lg h-12 pr-10 ${
                    isDark 
                      ? 'bg-slate-800/50 border-slate-700/50 focus:border-blue-500/50 text-slate-200'
                      : 'bg-gray-50 border-gray-300 focus:border-blue-500 text-gray-900'
                  }`}
                  autoFocus
                />
                <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm font-mono ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>%</span>
              </div>
              <Button
                data-testid="save-tax-rate-btn"
                onClick={handleSaveTaxRate}
                disabled={savingTax}
                className="bg-emerald-600 hover:bg-emerald-500 text-white h-12 px-4"
              >
                <Check size={18} className="mr-2" />
                {savingTax ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setEditingTax(false)}
                className={`h-12 px-4 ${
                  isDark 
                    ? 'border-slate-700/50 hover:bg-slate-800 text-slate-400'
                    : 'border-gray-300 hover:bg-gray-100 text-gray-600'
                }`}
              >
                <X size={18} />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span data-testid="tax-rate-display" className={`text-3xl font-mono font-bold ${isDark ? 'text-slate-200' : 'text-gray-900'}`}>{taxRate}%</span>
              <Button
                data-testid="edit-tax-rate-btn"
                variant="outline"
                onClick={() => { setTempTaxRate(String(taxRate)); setEditingTax(true); }}
                className={isDark 
                  ? 'border-slate-700/50 hover:bg-slate-800 text-slate-400 hover:text-slate-200'
                  : 'border-gray-300 hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                }
              >
                <Pencil size={16} className="mr-2" />
                Change
              </Button>
            </div>
          )}
          <p className={`text-xs mt-3 ${isDark ? 'text-slate-600' : 'text-gray-500'}`}>Used to calculate post-tax income from your earnings</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Post-tax $ is unbracketed & only calculated as a flat rate deduction of the chosen tax rate</p>
        </div>

        {/* wRVU Rate Config */}
        <div data-testid="wrvu-rate-config" className={`rounded-xl border p-6 ${
          isDark ? 'border-slate-700/30 bg-slate-800/30' : 'border-gray-200 bg-white'
        }`}>
          <div className="flex items-center gap-2 mb-4">
            <DollarSign size={18} className="text-emerald-400" />
            <span className={`text-sm uppercase tracking-widest font-semibold ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>wRVU Rate ({currentYear})</span>
          </div>
          {editingWrvu ? (
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm font-mono ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>$</span>
                <Input
                  data-testid="wrvu-rate-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={tempWrvuRate}
                  onChange={(e) => setTempWrvuRate(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleWrvuRateSubmit(); if (e.key === "Escape") setEditingWrvu(false); }}
                  className={`font-mono text-lg h-12 pl-8 ${
                    isDark 
                      ? 'bg-slate-800/50 border-slate-700/50 focus:border-emerald-500/50 text-slate-200'
                      : 'bg-gray-50 border-gray-300 focus:border-emerald-500 text-gray-900'
                  }`}
                  autoFocus
                />
              </div>
              <Button
                data-testid="save-wrvu-rate-btn"
                onClick={handleWrvuRateSubmit}
                disabled={savingWrvu}
                className="bg-emerald-600 hover:bg-emerald-500 text-white h-12 px-4"
              >
                <Check size={18} className="mr-2" />
                {savingWrvu ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setEditingWrvu(false)}
                className={`h-12 px-4 ${
                  isDark 
                    ? 'border-slate-700/50 hover:bg-slate-800 text-slate-400'
                    : 'border-gray-300 hover:bg-gray-100 text-gray-600'
                }`}
              >
                <X size={18} />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span data-testid="wrvu-rate-display" className={`text-3xl font-mono font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${wrvuRate}/wRVU</span>
              <Button
                data-testid="edit-wrvu-rate-btn"
                variant="outline"
                onClick={() => { setTempWrvuRate(String(wrvuRate)); setEditingWrvu(true); }}
                className={isDark 
                  ? 'border-slate-700/50 hover:bg-slate-800 text-slate-400 hover:text-slate-200'
                  : 'border-gray-300 hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                }
              >
                <Pencil size={16} className="mr-2" />
                Change
              </Button>
            </div>
          )}
          <p className={`text-xs mt-3 ${isDark ? 'text-slate-600' : 'text-gray-500'}`}>Dollar amount earned per wRVU for income calculation</p>
        </div>
      </div>

      {/* Clinic Locations Section */}
      <div className={`rounded-xl border p-6 ${
        isDark ? 'border-slate-700/30 bg-slate-800/30' : 'border-gray-200 bg-white'
      }`}>
        <div className="flex items-center gap-2 mb-6">
          <MapPin size={18} className="text-blue-400" />
          <span className={`text-sm uppercase tracking-widest font-semibold ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Clinic Locations</span>
        </div>

        {/* Add new location */}
        <div className="flex items-center gap-3 mb-6">
          <Input
            placeholder="Enter new clinic location..."
            value={newLocation}
            onChange={(e) => setNewLocation(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAddLocation(); }}
            className={`h-12 ${
              isDark 
                ? 'bg-slate-800/50 border-slate-700/50 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600'
                : 'bg-gray-50 border-gray-300 focus:border-blue-500 text-gray-900 placeholder:text-gray-400'
            }`}
          />
          <Button
            onClick={handleAddLocation}
            disabled={addingLocation || !newLocation.trim()}
            className="bg-blue-600 hover:bg-blue-500 text-white h-12 px-6"
          >
            <Plus size={18} className="mr-2" />
            {addingLocation ? "Adding..." : "Add"}
          </Button>
        </div>

        {/* Location list */}
        {loadingLocations ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-6 w-6 border-2 border-blue-400 border-t-transparent rounded-full" />
          </div>
        ) : clinicLocations.length === 0 ? (
          <div className="text-center py-8">
            <MapPin size={32} className={`mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />
            <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>No clinic locations added yet</p>
            <p className={`text-xs mt-1 ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Add your frequently used locations above</p>
          </div>
        ) : (
          <div className="space-y-2">
            {clinicLocations.map((location) => (
              <div
                key={location.id}
                className={`flex items-center justify-between px-4 py-3 rounded-lg border group transition-colors ${
                  isDark 
                    ? 'bg-slate-800/40 border-slate-700/30 hover:bg-slate-800/60'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <MapPin size={16} className="text-blue-400" />
                  <span className={isDark ? 'text-slate-200' : 'text-gray-900'}>{location.name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteLocation(location.id)}
                  className={`opacity-0 group-hover:opacity-100 transition-all ${
                    isDark 
                      ? 'text-slate-500 hover:text-red-400 hover:bg-red-500/10'
                      : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                  }`}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            ))}
          </div>
        )}
        <p className={`text-xs mt-4 ${isDark ? 'text-slate-600' : 'text-gray-500'}`}>These locations will appear as suggestions when entering shift data</p>
      </div>

      {/* Quick-Add Visit Types Section */}
      <div data-testid="quick-add-visits-config" className={`rounded-xl border p-6 ${
        isDark ? 'border-slate-700/30 bg-slate-800/30' : 'border-gray-200 bg-white'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          <Stethoscope size={18} className="text-cyan-400" />
          <span className={`text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>Quick-Add Visit Types</span>
        </div>
        <p className={`text-sm mb-6 ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>Customize the 6 CPT codes displayed in the Quick-Add Visits section on the Tracker page</p>

        {loadingVisits ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-6 w-6 border-2 border-cyan-400 border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-3">
            {quickAddVisits.map((visit, index) => (
              <div key={index}>
                {editingVisitIndex === index ? (
                  /* Edit Mode */
                  <div className={`p-4 rounded-lg border space-y-4 ${
                    isDark ? 'bg-slate-800/60 border-cyan-500/30' : 'bg-cyan-50 border-cyan-200'
                  }`}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* CPT Code */}
                      <div>
                        <label className={`text-xs mb-1 block ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>CPT Code *</label>
                        <Input
                          value={tempVisit.code}
                          onChange={(e) => handleTempVisitChange("code", e.target.value)}
                          placeholder="e.g., 99213"
                          className={`h-10 ${
                            isDark 
                              ? 'bg-slate-900/50 border-slate-700/50 focus:border-cyan-500/50 text-slate-200'
                              : 'bg-white border-gray-300 focus:border-cyan-500 text-gray-900'
                          }`}
                        />
                      </div>
                      {/* Description */}
                      <div className="sm:col-span-2">
                        <label className={`text-xs mb-1 block ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Description</label>
                        <Input
                          value={tempVisit.description}
                          onChange={(e) => handleTempVisitChange("description", e.target.value)}
                          placeholder="e.g., Established Patient, Level 3"
                          className={`h-10 ${
                            isDark 
                              ? 'bg-slate-900/50 border-slate-700/50 focus:border-cyan-500/50 text-slate-200'
                              : 'bg-white border-gray-300 focus:border-cyan-500 text-gray-900'
                          }`}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-end gap-4">
                      {/* wRVU */}
                      <div className="w-32">
                        <label className={`text-xs mb-1 block ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>wRVU per Visit</label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={tempVisit.wrvu}
                          onChange={(e) => handleTempVisitChange("wrvu", parseFloat(e.target.value) || 0)}
                          className={`h-10 ${
                            isDark 
                              ? 'bg-slate-900/50 border-slate-700/50 focus:border-cyan-500/50 text-slate-200'
                              : 'bg-white border-gray-300 focus:border-cyan-500 text-gray-900'
                          }`}
                        />
                      </div>
                      {/* Color Selection */}
                      <div>
                        <label className={`text-xs mb-1 block ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Card Color</label>
                        <div className="flex items-center gap-2">
                          {COLOR_OPTIONS.map((colorOpt) => (
                            <button
                              key={colorOpt.value}
                              type="button"
                              onClick={() => handleTempVisitChange("color", colorOpt.value)}
                              className={`w-8 h-8 rounded-full ${colorOpt.bgClass} transition-all ${
                                tempVisit.color === colorOpt.value 
                                  ? `ring-2 ring-offset-2 ${isDark ? 'ring-offset-slate-800' : 'ring-offset-cyan-50'} ${colorOpt.borderClass} scale-110` 
                                  : "opacity-60 hover:opacity-100"
                              }`}
                              title={colorOpt.label}
                            />
                          ))}
                        </div>
                      </div>
                      {/* Action buttons */}
                      <div className="flex items-center gap-2 ml-auto">
                        <Button
                          onClick={handleSaveVisit}
                          disabled={savingVisit}
                          className="bg-cyan-600 hover:bg-cyan-500 text-white h-10 px-4"
                        >
                          <Check size={16} className="mr-1" />
                          {savingVisit ? "Saving..." : "Save"}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleCancelEditVisit}
                          className={`h-10 px-4 ${
                            isDark 
                              ? 'border-slate-700/50 hover:bg-slate-800 text-slate-400'
                              : 'border-gray-300 hover:bg-gray-100 text-gray-600'
                          }`}
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Display Mode */
                  <div 
                    className={`flex items-center justify-between px-4 py-3 rounded-lg border group transition-colors ${
                      isDark 
                        ? `hover:bg-slate-800/60 ${getVisitColorClasses(visit.color)}`
                        : `hover:bg-gray-50 ${getVisitColorClasses(visit.color)}`
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <span className={`font-mono font-bold text-lg ${isDark ? '' : 'text-gray-900'}`}>{visit.code}</span>
                      <span className={`text-sm hidden sm:inline ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{visit.description}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`font-mono text-sm ${isDark ? '' : 'text-gray-700'}`}>{visit.wrvu} wRVU</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditVisit(index)}
                        className={`opacity-0 group-hover:opacity-100 transition-all ${
                          isDark 
                            ? 'text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10'
                            : 'text-gray-400 hover:text-cyan-600 hover:bg-cyan-50'
                        }`}
                      >
                        <Pencil size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <p className={`text-xs mt-4 ${isDark ? 'text-slate-600' : 'text-gray-500'}`}>Click the pencil icon to edit any visit type. Changes will update the Tracker page immediately.</p>
      </div>

      {/* Subscription Management Section */}
      <div data-testid="subscription-management" className={`rounded-xl border p-6 ${
        isDark ? 'border-slate-700/30 bg-slate-800/30' : 'border-gray-200 bg-white'
      }`}>
        <div className="flex items-center gap-3 mb-5">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border ${
            isDark ? 'bg-purple-500/15 border-purple-500/20' : 'bg-purple-50 border-purple-200'
          }`}>
            <CreditCard size={18} className="text-purple-400" />
          </div>
          <div>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>Subscription & Billing</h3>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Manage your plan and payment method.</p>
          </div>
        </div>

        {/* Current plan badge */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Current plan:</span>
          {subBadge ? (
            <span data-testid="sub-plan-badge" className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${subBadge.color}`}>
              {subStatus?.status === "lifetime" && <Crown size={12} />}
              {subStatus?.status === "trial" && <Zap size={12} />}
              {subBadge.label}
            </span>
          ) : (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${isDark ? 'text-slate-500 bg-slate-700/30 border-slate-600/30' : 'text-gray-500 bg-gray-100 border-gray-200'}`}>
              <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full" />
              Loading...
            </span>
          )}
        </div>

        {/* Payment failed warning */}
        {subStatus?.status === "payment_failed" && (
          <div className={`flex items-start gap-3 rounded-lg border p-4 mb-4 ${
            isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
          }`}>
            <div className="text-red-400 mt-0.5">⚠</div>
            <div>
              <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-red-300' : 'text-red-700'}`}>Payment Failed</p>
              <p className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                Your last payment was declined. Please update your payment method to restore access to the app.
              </p>
            </div>
          </div>
        )}

        {/* Trial info */}
        {subStatus?.status === "trial" && (
          <p data-testid="trial-info" className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
            Your free trial ends in <strong>{subStatus.days_remaining} day{subStatus.days_remaining !== 1 ? "s" : ""}</strong>. You can cancel before then to avoid being charged.
          </p>
        )}

        {/* Lifetime info */}
        {subStatus?.status === "lifetime" && (
          <p data-testid="lifetime-info" className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
            You have lifetime access — no recurring charges, no expiration.
          </p>
        )}

        {/* Admin info */}
        {subStatus?.status === "admin" && (
          <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
            You have complimentary admin access to all features.
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          {/* Manage via Stripe portal (monthly / yearly / trial / payment_failed) */}
          {canManage && (
            <Button
              data-testid="manage-subscription-btn"
              onClick={handleManageSubscription}
              disabled={loadingPortal}
              className={`flex items-center gap-2 font-semibold ${
                subStatus?.status === "payment_failed"
                  ? 'bg-red-600 hover:bg-red-500 text-white'
                  : isDark
                    ? 'bg-slate-700 hover:bg-slate-600 text-slate-100 border border-slate-600'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300'
              }`}
            >
              {loadingPortal ? (
                <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
              ) : (
                <ExternalLink size={15} />
              )}
              {subStatus?.status === "payment_failed" ? "Update Payment Method" : "Manage Subscription"}
            </Button>
          )}

          {/* Upgrade to Lifetime */}
          {canUpgradeToLifetime && (
            <Button
              data-testid="upgrade-lifetime-btn"
              onClick={handleUpgradeToLifetime}
              disabled={loadingUpgrade}
              className="flex items-center gap-2 font-semibold bg-purple-600 hover:bg-purple-500 text-white"
            >
              {loadingUpgrade ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Crown size={15} />
              )}
              Upgrade to Lifetime — $199
            </Button>
          )}
        </div>

        {/* What the portal does */}
        {canManage && subStatus?.status !== "payment_failed" && (
          <p className={`text-xs mt-4 ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
            "Manage Subscription" opens your secure Stripe billing portal — cancel your plan or free trial, switch between Monthly and Annual, or update your payment method.
          </p>
        )}
      </div>

      {/* Contact Admin Section */}
      <div className={`rounded-xl border p-6 ${
        isDark ? 'bg-slate-800/30 border-slate-700/30' : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border ${
            isDark ? 'bg-blue-500/15 border-blue-500/20' : 'bg-blue-50 border-blue-200'
          }`}>
            <MessageSquare size={18} className="text-blue-400" />
          </div>
          <div>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>Questions or Concerns?</h3>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Message Admin</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Textarea
              data-testid="admin-message-input"
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              placeholder="Type your message here..."
              maxLength={1000}
              className={`min-h-[120px] resize-none ${
                isDark 
                  ? 'bg-slate-900/50 border-slate-700/50 focus:border-blue-500/50 text-slate-200 placeholder:text-slate-600'
                  : 'bg-gray-50 border-gray-300 focus:border-blue-500 text-gray-900 placeholder:text-gray-400'
              }`}
            />
            <div className="flex items-center justify-between mt-2">
              <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{messageContent.length}/1000 characters</span>
              <Button
                data-testid="send-admin-message-btn"
                onClick={handleSendMessage}
                disabled={sendingMessage || !messageContent.trim()}
                className="bg-blue-600 hover:bg-blue-500 text-white font-semibold"
              >
                {sendingMessage ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <>
                    <Send size={16} className="mr-1.5" />
                    Send Message
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* My Messages Section */}
          <div className={`border-t pt-4 ${isDark ? 'border-slate-700/30' : 'border-gray-200'}`}>
            <button
              onClick={() => { setShowMessages(!showMessages); if (!showMessages) loadMyMessages(); }}
              className={`flex items-center gap-2 text-sm transition-colors ${
                isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {showMessages ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              View my previous messages
            </button>

            {showMessages && (
              <div className="mt-4 space-y-3">
                {loadingMessages ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin h-5 w-5 border-2 border-blue-400 border-t-transparent rounded-full" />
                  </div>
                ) : myMessages.length === 0 ? (
                  <p className={`text-sm text-center py-4 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>No messages yet</p>
                ) : (
                  myMessages.map((msg) => (
                    <div key={msg.id} className={`rounded-lg border p-4 ${
                      isDark ? 'bg-slate-900/50 border-slate-700/30' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{formatMessageDate(msg.created_at)}</span>
                        {msg.replies?.length > 0 && (
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                            Replied
                          </span>
                        )}
                      </div>
                      <p className={`text-sm whitespace-pre-wrap ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{msg.content}</p>
                      
                      {/* Admin replies */}
                      {msg.replies?.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {msg.replies.map((reply) => (
                            <div key={reply.id} className="pl-3 border-l-2 border-emerald-500/30 ml-2">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-emerald-400">Admin Reply</span>
                                <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{formatMessageDate(reply.created_at)}</span>
                              </div>
                              <p className={`text-sm whitespace-pre-wrap ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{reply.content}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for wRVU Rate */}
      {showWrvuConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div data-testid="wrvu-confirm-dialog" className={`rounded-2xl p-6 max-w-md mx-4 shadow-2xl relative border ${
            isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border ${
                isDark ? 'bg-emerald-500/15 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'
              }`}>
                <DollarSign size={20} className="text-emerald-400" />
              </div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>Apply New Rate</h3>
            </div>
            <p className={`text-sm mb-6 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              Where would you like to apply the new rate of <span className={`font-mono font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>${pendingWrvuRate}/wRVU</span>?
            </p>
            <div className="flex flex-col gap-3">
              <Button
                data-testid="wrvu-confirm-previous"
                onClick={() => handleSaveWrvuRate(true)}
                disabled={savingWrvu}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
              >
                {savingWrvu ? "Saving..." : "Apply to All Previous and Future Shifts"}
              </Button>
              <Button
                data-testid="wrvu-confirm-future"
                onClick={() => handleSaveWrvuRate(false)}
                disabled={savingWrvu}
                className="w-full bg-red-600 hover:bg-red-500 text-white font-semibold"
              >
                Apply only to Future Shifts
              </Button>
            </div>
            <button
              onClick={() => { setShowWrvuConfirm(false); setPendingWrvuRate(null); }}
              className={`absolute top-4 right-4 ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
